import { useCallback, useEffect, useState } from 'react';
import { KpiTile, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

interface EndUserRow {
  id: number;
  email: string;
  phone: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  no_show_count: number;
  total_completed: number;
  tier: 'green' | 'yellow' | 'red';
  blacklisted: boolean;
}

interface ReputationDetail {
  end_user_id: number;
  no_show_count: number;
  total_completed: number;
  last_no_show_at: string | null;
  tier: 'green' | 'yellow' | 'red';
  blacklisted: boolean;
  guaranteed: boolean;
  requires_sms_confirmation: boolean;
  requires_card_guarantee: boolean;
  reset_eligible: boolean;
}

interface PassportKpis {
  total_end_users: number;
  active_users: number;
  verified_users: number;
  by_tier: { green: number; yellow: number; red: number };
  total_wallet_balance_cents: number;
  total_reviews: number;
  total_reservations_completed: number;
  generated_at: string;
}

interface PassportHistoryEntry {
  id: number;
  type: 'booking' | 'checkin' | 'treatment' | 'class' | 'visit' | 'cancel' | 'no_show' | 'loyalty' | 'coupon' | 'notification' | 'preference_update' | 'review';
  vertical: string;
  store_id: number;
  store_name: string;
  category_code: string | null;
  description: string;
  amount_cents: number | null;
  status: string | null;
  created_at: string;
}

interface BadgeResult {
  badge_code: string;
  badge_name: string;
  badge_description: string;
  badge_icon: string;
  earned: boolean;
  earned_at: string | null;
  progress: number;
  target: number;
}

interface WalletSnapshot {
  end_user_id: number;
  balance_cents: number;
  total_earned_cents: number;
  total_spent_cents: number;
  active_fiches: Array<{ id: number; amount_cents: number; expires_at: string }>;
  recent_transactions: Array<{
    id: number;
    type: string;
    amount_cents: number;
    reason: string | null;
    created_at: string;
  }>;
}

const TIER_LABELS: Record<string, { label: string; cls: string }> = {
  green: { label: 'GREEN', cls: 'sk-tier sk-tier--green' },
  yellow: { label: 'YELLOW', cls: 'sk-tier sk-tier--yellow' },
  red: { label: 'RED', cls: 'sk-tier sk-tier--red' },
};

const HISTORY_TYPE_LABEL: Record<string, string> = {
  booking: 'Prenotazione',
  checkin: 'Check-in',
  treatment: 'Trattamento',
  class: 'Corso',
  visit: 'Visita',
  cancel: 'Cancellazione',
  no_show: 'No-show',
  loyalty: 'Loyalty',
  coupon: 'Coupon',
  notification: 'Notifica',
  preference_update: 'Preferenze',
  review: 'Recensione',
};

export function MasterEndUsersPage() {
  const [users, setUsers] = useState<EndUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [reputation, setReputation] = useState<ReputationDetail | null>(null);
  const [badges, setBadges] = useState<BadgeResult[]>([]);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [kpis, setKpis] = useState<PassportKpis | null>(null);
  const [history, setHistory] = useState<PassportHistoryEntry[]>([]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/v1/master/end-users?limit=50&search=${encodeURIComponent(search)}`);
      if (!res.ok) throw new Error('Failed to load end users');
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadUsers();
    apiFetch('/api/v1/master/passport/kpis').then(async (r) => {
      if (r.ok) setKpis(await r.json());
    }).catch(() => {});
  }, [loadUsers]);

  const loadDetail = async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    setActionMsg('');
    try {
      const [repRes, badgeRes, walletRes, histRes] = await Promise.all([
        apiFetch(`/api/v1/master/end-users/${id}/reputation`),
        apiFetch(`/api/v1/master/end-users/${id}/badges`),
        apiFetch(`/api/v1/master/end-users/${id}/wallet`),
        apiFetch(`/api/v1/master/passport/${id}/history?limit=20`),
      ]);
      if (repRes.ok) setReputation(await repRes.json());
      if (badgeRes.ok) {
        const bd = await badgeRes.json();
        setBadges(bd.badges || []);
      }
      if (walletRes.ok) setWallet(await walletRes.json());
      if (histRes.ok) {
        const hd = await histRes.json();
        setHistory(hd.entries || []);
      }
    } catch {
      // soft-fail
    } finally {
      setDetailLoading(false);
    }
  };

  const resetReputation = async () => {
    if (!selectedId) return;
    if (!confirm('Resettare la reputation di questo utente? Tornerà al tier GREEN.')) return;
    try {
      const res = await apiFetch(`/api/v1/master/end-users/${selectedId}/reset-reputation`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setActionMsg(data.message || 'Reputation resettata');
        loadDetail(selectedId);
        loadUsers();
      }
    } catch {
      setActionMsg('Errore nel reset');
    }
  };

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Passaporto End User"
        subtitle="Identità cross-tenant: reputation, wallet fiches, badge, storico prenotazioni/recensioni."
      />

      {/* KPI Row */}
      {kpis ? (
        <div className="sk-mc-grid sk-mc-grid--kpi">
          <KpiTile label="End users" value={kpis.total_end_users} sub={`${kpis.active_users} attivi · ${kpis.verified_users} verificati`} tone="info" icon={<span>👤</span>} />
          <KpiTile label="Tier GREEN" value={kpis.by_tier.green} sub="Affidabili" tone="ok" icon={<span>✓</span>} />
          <KpiTile label="Tier YELLOW" value={kpis.by_tier.yellow} sub="1-2 no-show" tone="warn" icon={<span>⚠</span>} />
          <KpiTile label="Tier RED" value={kpis.by_tier.red} sub="Blacklist" tone="error" icon={<span>✕</span>} />
          <KpiTile label="Wallet totale" value={`€${(kpis.total_wallet_balance_cents / 100).toFixed(0)}`} sub="Fiches in circolazione" tone="info" icon={<span>🪙</span>} />
          <KpiTile label="Recensioni" value={kpis.total_reviews} sub="Verificate" tone="ok" icon={<span>⭐</span>} />
        </div>
      ) : null}

      <div className="sk-master-endusers">
        <div className="sk-master-endusers__list">
          <div className="sk-search-bar">
            <input
              type="text"
              placeholder="Cerca per email, nome, telefono…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="sk-input"
            />
            <button onClick={loadUsers} className="sk-btn sk-btn--primary">
              Cerca
            </button>
          </div>

          {loading && <p className="sk-muted">Caricamento…</p>}
          {error && <p className="sk-error">{error}</p>}

          {!loading && users.length === 0 && <p className="sk-muted">Nessun utente trovato.</p>}

          {users.length > 0 && (
            <table className="sk-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Tier</th>
                  <th>No-show</th>
                  <th>Completate</th>
                  <th>Registrato</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => loadDetail(u.id)}
                    className={selectedId === u.id ? 'sk-row--selected' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{u.id}</td>
                    <td>{u.display_name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={TIER_LABELS[u.tier]?.cls || 'sk-tier'}>{TIER_LABELS[u.tier]?.label || u.tier}</span>
                      {u.blacklisted && <span className="sk-badge sk-badge--red">BL</span>}
                    </td>
                    <td>{u.no_show_count}</td>
                    <td>{u.total_completed}</td>
                    <td>{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="sk-muted sk-muted--small">{total} utenti totali</p>
        </div>

        {selectedId && (
          <div className="sk-master-endusers__detail">
            <h2>Dettaglio utente #{selectedId}</h2>
            {detailLoading && <p className="sk-muted">Caricamento…</p>}
            {actionMsg && <p className="sk-success">{actionMsg}</p>}

            {reputation && (
              <SectionCard title="Reputation (Anti-Bidone)">
                <p>
                    <strong>Tier:</strong>{' '}
                    <span className={TIER_LABELS[reputation.tier]?.cls}>{TIER_LABELS[reputation.tier]?.label}</span>
                  </p>
                  <p><strong>No-show:</strong> {reputation.no_show_count}</p>
                  <p><strong>Completate:</strong> {reputation.total_completed}</p>
                  <p><strong>Ultimo no-show:</strong> {reputation.last_no_show_at ? new Date(reputation.last_no_show_at).toLocaleString('it-IT') : 'mai'}</p>
                  <p><strong>Blacklist:</strong> {reputation.blacklisted ? 'Sì' : 'No'}</p>
                  <p><strong>Garantita:</strong> {reputation.guaranteed ? 'Sì' : 'No'}</p>
                  <p><strong>Reset eligible:</strong> {reputation.reset_eligible ? 'Sì' : 'No'}</p>
                  <button onClick={resetReputation} className="sk-btn sk-btn--warning">
                    Reset Reputation
                  </button>
              </SectionCard>
            )}

            {badges.length > 0 && (
              <SectionCard title={`Passaporto StrikerPos (${badges.filter((b) => b.earned).length}/${badges.length})`}>
                  {badges.map((b) => (
                    <div key={b.badge_code} className={`sk-badge-item ${b.earned ? 'sk-badge-item--earned' : ''}`}>
                      <span className="sk-badge-item__icon">{b.earned ? '★' : '○'}</span>
                      <div>
                        <p className="sk-badge-item__name">{b.badge_name}</p>
                        <p className="sk-badge-item__desc">{b.badge_description}</p>
                        <p className="sk-badge-item__progress">{b.progress}/{b.target}</p>
                      </div>
                    </div>
                  ))}
              </SectionCard>
            )}

            {wallet && (
              <SectionCard title="Wallet Fiches">
                  <p className="sk-wallet-balance">
                    <strong>Saldo:</strong> {(wallet.balance_cents / 100).toFixed(2)}€
                  </p>
                  <p><strong>Total guadagnato:</strong> {(wallet.total_earned_cents / 100).toFixed(2)}€</p>
                  <p><strong>Total speso:</strong> {(wallet.total_spent_cents / 100).toFixed(2)}€</p>
                  <p><strong>Fiches attive:</strong> {wallet.active_fiches.length}</p>
                  {wallet.active_fiches.map((f) => (
                    <p key={f.id} className="sk-muted sk-muted--small">
                      {f.amount_cents} cents — scade {new Date(f.expires_at).toLocaleDateString('it-IT')}
                    </p>
                  ))}
                  {wallet.recent_transactions.length > 0 && (
                    <details>
                      <summary>Transazioni recenti ({wallet.recent_transactions.length})</summary>
                      <ul className="sk-tx-list">
                        {wallet.recent_transactions.map((tx) => (
                          <li key={tx.id} className="sk-tx-item">
                            <span className={`sk-tx-type sk-tx-type--${tx.type}`}>{tx.type}</span>
                            <span>{tx.amount_cents > 0 ? '+' : ''}{tx.amount_cents} cents</span>
                            <span className="sk-muted">{tx.reason || ''}</span>
                            <span className="sk-muted">{new Date(tx.created_at).toLocaleString('it-IT')}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
              </SectionCard>
            )}

            {/* Storico cross-verticale */}
            {history.length > 0 && (
              <SectionCard title="Storico cross-verticale">
                <ul className="sk-mc-feed">
                  {history.map((h) => (
                    <li key={`${h.type}-${h.id}`} className="sk-mc-feed__item">
                      <span className={`sk-mc-feed__dot sk-mc-feed__dot--${h.type}`} />
                      <div className="sk-mc-feed__body">
                        <p className="sk-mc-feed__action">
                          <strong>{HISTORY_TYPE_LABEL[h.type] || h.type}</strong>
                          {' · '}
                          {h.store_name}
                          {h.category_code ? ` (${h.category_code})` : ''}
                        </p>
                        <p className="sk-mc-feed__meta">
                          {h.description}
                          {h.amount_cents != null ? ` · ${h.amount_cents > 0 ? '+' : ''}${h.amount_cents} cents` : ''}
                          {h.status ? ` · ${h.status}` : ''}
                          {' · '}
                          {new Date(h.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </SectionCard>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
